import React, { Component, Suspense } from 'react';
import { Route, Switch, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import * as slidesActions from '../actions/index';
import * as userActions from '../../global/user/actions/index';

import SubNav from '../sub_nav/sub_nav';

const SectionHeader = React.lazy(() => import('../../global/table_elements/section_header/section_header'));
const TableRow = React.lazy(() => import('../../global/table_elements/table_row/table_row'));

const EditorIndex = React.lazy(() => import('../editor_index/editor_index'));
const EditorView = React.lazy(() => import('../editor_view/editor_view'));
const AiDraftReview = React.lazy(() => import('../ai_draft_review/ai_draft_review'));
import LoadingIndicator from '../../global/loading_indicator/loading_indicator';
import ThemeHelpBox from '../themes/theme_help_box';

class EditorLayout extends Component {

    constructor(props) {
        super(props);
        this.authTokenCallback = this.authTokenCallback.bind(this);
        this.state = {
            contextMenu: null,
            renamingDocId: null,
            renamingName: '',
        };
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
    }

    componentDidMount() {
        this.props.onUpdateAuthToken(this.authTokenCallback);
        document.addEventListener('mousedown', this.handleOutsideClick);

        // Check for draft data passed from the converter page via sessionStorage
        const storedDraft = sessionStorage.getItem('wk_ai_draft');
        if (storedDraft) {
            sessionStorage.removeItem('wk_ai_draft');
            try {
                const draft = JSON.parse(storedDraft);
                this.props.onLoadDraftFromStorage(draft);
            } catch (e) {
                // Ignore parse errors
            }
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleOutsideClick);
    }

    handleOutsideClick(e) {
        if (this.state.contextMenu && !e.target.closest('.context-menu')) {
            this.setState({ contextMenu: null });
        }
    }

    handleContextMenu = (e, doc) => {
        e.preventDefault();
        this.setState({
            contextMenu: { docId: doc.id, docName: doc.name, x: e.clientX, y: e.clientY },
        });
    }

    closeContextMenu = () => {
        this.setState({ contextMenu: null });
    }

    handleRename = (docId, docName) => {
        this.setState({
            renamingDocId: docId,
            renamingName: docName || '',
            contextMenu: null,
        });
    }

    handleRenameSubmit = () => {
        const { renamingDocId, renamingName } = this.state;
        const trimmed = renamingName.trim();
        if (trimmed && renamingDocId) {
            this.props.onSaveDocument(renamingDocId, null, trimmed);
        }
        this.setState({ renamingDocId: null, renamingName: '' });
    }

    handleRenameCancel = () => {
        this.setState({ renamingDocId: null, renamingName: '' });
    }

    handleDelete = (docId) => {
        this.setState({ contextMenu: null });
        if (!window.confirm('Delete this document?')) return;
        this.props.onDeleteDocument(docId, () => {
            const currentPath = this.props.location.pathname;
            if (currentPath === '/slides/editor/' + docId) {
                this.props.history.push('/slides/editor');
            }
        });
    }

    authTokenCallback() {
        this.props.onFetchDocuments();
        if (this.props.documentId) {
            this.props.history.push('/slides/editor/' + this.props.documentId);
        }
    }

    goToPage = (pageLink) => {
        this.props.history.push(pageLink);
    }

    formatDate(dateStr) {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString();
    }

    rightSideComponent() {
        // Show draft review when AI draft data exists in Redux
        if (this.props.draft_slides) {
            return (
                <AiDraftReview
                    draft_name={this.props.draft_name}
                    draft_slides={this.props.draft_slides}
                    draft_images={this.props.draft_images}
                    generating={this.props.generating_from_draft}
                    generate_error={this.props.generate_draft_error}
                    onUpdateDraft={(slides) => this.props.onUpdateDraftSlides(slides)}
                    onGenerateFromDraft={(name, slides, images, addBlankSlides, callback) => {
                        this.props.onGenerateFromDraft(name, slides, images, addBlankSlides, callback);
                    }}
                    onNavigateToDocument={(docId) => this.goToPage('/slides/editor/' + docId)}
                    onCancelDraft={() => this.props.onClearDraft()}
                />
            );
        }

        return (
            <Switch>
                <Route path="/slides" exact component={EditorIndex} />
                <Route path="/slides/editor" exact component={EditorIndex} />
                <Route path="/slides/editor/:id" component={EditorView} />
            </Switch>
        );
    }

    render() {
        let documents = this.props.documents || [];

        const hasDocuments = documents.length > 0;

        let documentsSection = (
            <div>
                <Suspense fallback={<LoadingIndicator />}>
                    <SectionHeader
                        text="Documents"
                        accessory={hasDocuments ? (
                            <p>
                                <a
                                    className="text-sm text-blue-500 cursor-pointer hover:bg-blue-500 hover:text-white rounded-sm p-1 px-2"
                                    onClick={() => {
                                        this.props.onCreateDocument(null, (doc) => {
                                            this.goToPage('/slides/editor/' + doc.id);
                                        });
                                    }}
                                >
                                    New
                                </a>
                            </p>
                        ) : null}
                    />
                    {!hasDocuments && !this.props.loading_documents && (
                        <div className="px-4 py-3">
                            <div className="bg-gray-100 flex flex-col items-center justify-center rounded-3xl" style={{ aspectRatio: '16/9' }}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                    <polyline points="10 9 9 9 8 9" />
                                </svg>
                                <a
                                    className="mt-3 text-sm cursor-pointer bg-blue-500 rounded-sm p-1 px-3" style={{ color: 'white' }}
                                    onClick={() => {
                                        this.props.onCreateDocument(null, (doc) => {
                                            this.goToPage('/slides/editor/' + doc.id);
                                        });
                                    }}
                                >
                                    New Document
                                </a>
                            </div>
                        </div>
                    )}
                    {documents.map((doc) => (
                        <Route key={doc.id} exact path={"/slides/editor/" + doc.id} children={({ match }) => (
                            this.state.renamingDocId === doc.id ? (
                                <div className="px-4 py-2">
                                    <input
                                        type="text"
                                        className="w-full border border-blue-400 rounded-sm px-2 py-1 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-400"
                                        value={this.state.renamingName}
                                        onChange={(e) => this.setState({ renamingName: e.target.value })}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') this.handleRenameSubmit();
                                            if (e.key === 'Escape') this.handleRenameCancel();
                                        }}
                                        onBlur={this.handleRenameSubmit}
                                        autoFocus
                                        onFocus={(e) => e.target.select()}
                                    />
                                </div>
                            ) : (
                                <div onContextMenu={(e) => this.handleContextMenu(e, doc)}>
                                    <TableRow
                                        key={"doc_" + doc.id}
                                        selected={match}
                                        clickEvent={() => this.goToPage("/slides/editor/" + doc.id)}
                                        text={doc.name}
                                        detail_text={this.formatDate(doc.updated_at)}
                                    />
                                </div>
                            )
                        )} />
                    ))}
                </Suspense>
            </div>
        );

        if (this.props.loading_documents) {
            documentsSection = <div></div>;
        }

        const { contextMenu } = this.state;

        return (
            <div className="flex flex-col" style={{ height: 'calc(100vh - 120px)' }}>
                <SubNav />
                <div className="flex flex-1 min-h-0 bg-white rounded-sm shadow-md border-b-2">
                    {/* Left pane: document list */}
                    <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
                        <ThemeHelpBox hasCustomTheme={false} />
                        {documentsSection}
                    </div>
                    {/* Right pane: editor or index */}
                    <div className="w-2/3 overflow-y-auto">
                        <Suspense fallback={<LoadingIndicator />}>
                            {this.rightSideComponent()}
                        </Suspense>
                    </div>
                </div>
                {contextMenu && (
                    <div
                        className="context-menu fixed bg-white shadow-lg rounded-sm border border-gray-200 py-1 z-50"
                        style={{ left: contextMenu.x, top: contextMenu.y }}
                    >
                        <button
                            className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                            onClick={() => this.handleRename(contextMenu.docId, contextMenu.docName)}
                        >
                            Rename
                        </button>
                        <button
                            className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100"
                            onClick={() => this.handleDelete(contextMenu.docId)}
                        >
                            Delete
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        loading_documents: state.slides.loading_documents,
        documents: state.slides.documents,
        draft_name: state.slides.draft_name,
        draft_slides: state.slides.draft_slides,
        draft_images: state.slides.draft_images,
        generating_from_draft: state.slides.generating_from_draft,
        generate_draft_error: state.slides.generate_draft_error,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchDocuments: () => dispatch(slidesActions.fetchDocuments()),
        onCreateDocument: (name, callback) => dispatch(slidesActions.createDocument(name, callback)),
        onDeleteDocument: (id, callback) => dispatch(slidesActions.deleteDocument(id, callback)),
        onSaveDocument: (id, presentationJson, name) => dispatch(slidesActions.saveDocument(id, presentationJson, name)),
        onUpdateAuthToken: (callback) => dispatch(userActions.updateAuthToken(callback)),
        onUpdateDraftSlides: (slides) => dispatch(slidesActions.updateDraftSlides(slides)),
        onGenerateFromDraft: (name, slides, images, addBlankSlides, callback) => dispatch(slidesActions.generateFromDraft(name, slides, images, addBlankSlides, callback)),
        onClearDraft: () => dispatch(slidesActions.clearDraft()),
        onLoadDraftFromStorage: (draft) => dispatch({ type: 'AI_DRAFT_SUCCESS', draft }),
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EditorLayout));
